import { Field } from '@/components/ui';
import type { TimeBlock } from '@/components/WeekGrid';
import { describeBlocks } from '@/lib/describePlan';

export type CatalogueService = {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  external_id?: string | null;
  active: boolean;
};

export type Frequency =
  | { kind: 'every_visit' }
  // Free text. A task's days are described rather than drawn: the grid belongs
  // to the plan's default schedule, and most tasks follow it.
  | { kind: 'days'; text?: string; days?: string[]; blocks?: TimeBlock[] }
  | { kind: 'as_needed' }
  | { kind: 'once' };

const KINDS = [
  { value: 'every_visit', label: 'Every visit', hint: 'Whenever a caregiver attends.' },
  { value: 'days', label: 'Specific days', hint: 'Named weekdays.' },
  { value: 'as_needed', label: 'As needed', hint: 'Offered, not owed.' },
  { value: 'once', label: 'Once', hint: 'A single occurrence.' },
] as const;

/**
 * A task's service.
 *
 * Plans used to name services in free text, so "personal care" meant whatever
 * each coordinator typed and nothing could be reported across clients. The
 * value stored is the catalogue's internal id, never the agency's own code —
 * that mapping lives on the service and can change without touching a plan.
 */
export function ServicePicker({
  value,
  services,
  onChange,
}: {
  value: string | undefined;
  services: CatalogueService[];
  onChange: (serviceId: string) => void;
}) {
  // A retired service stays selectable while it is the current value, or
  // editing an older plan would silently drop the service it names.
  const retained = value && !services.some((s) => s.id === value);

  return (
    <Field
      label="Service"
      hint="Which of the agency's services this task delivers. Required before publishing."
    >
      {(props) => (
        <select
          {...props}
          className="input"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Choose a service…</option>
          {services.map((service) => (
            <option key={service.id} value={service.id}>
              {service.name}
            </option>
          ))}
          {retained ? <option value={value}>(retired service)</option> : null}
        </select>
      )}
    </Field>
  );
}

/**
 * A risk's category.
 *
 * Risks were typed, so "fall risk" and "Falls" and "unsteady on stairs" were
 * three different things to anyone counting. The category is chosen; the
 * specifics stay free text beside it, because "keeps the landing light on" is
 * exactly the detail a standard list cannot hold.
 */
export function RiskPicker({
  value,
  risks,
  onChange,
}: {
  value: string | undefined;
  risks: CatalogueService[];
  onChange: (riskId: string, name: string | null) => void;
}) {
  const retained = value && !risks.some((r) => r.id === value);
  const chosen = risks.find((r) => r.id === value);

  return (
    <Field
      label="Risk"
      hint={chosen?.description ?? 'Choose the category. Describe the specifics below.'}
    >
      {(props) => (
        <select
          {...props}
          className="input"
          value={value ?? ''}
          onChange={(event) => {
            const id = event.target.value;
            onChange(id, risks.find((r) => r.id === id)?.name ?? null);
          }}
        >
          <option value="">Choose a risk…</option>
          {risks.map((risk) => (
            <option key={risk.id} value={risk.id}>
              {risk.name}
            </option>
          ))}
          {retained ? <option value={value}>(retired risk)</option> : null}
        </select>
      )}
    </Field>
  );
}

/**
 * How often a task happens.
 *
 * Replaces a free-text box that accepted "each visit", "when willing", and
 * anything else a coordinator typed — none of which could decide whether a task
 * was due or be compared between two clients.
 *
 * Days belong to the frequency rather than sitting beside it. They used to live
 * in three places at once: on the task, in the plan's schedule, and on the real
 * visits, free to disagree with nothing reconciling them.
 */
export function FrequencyPicker({
  value,
  onChange,
  inherited,
}: {
  value: Frequency | string | undefined;
  onChange: (next: Frequency) => void;
  /** The plan's default week, shown behind an empty task grid. */
  inherited?: TimeBlock[];
}) {
  const current: Frequency =
    typeof value === 'object' && value !== null && 'kind' in value
      ? value
      : { kind: 'every_visit' };

  const text = current.kind === 'days' ? current.text ?? '' : '';
  const legacy = typeof value === 'string' && value.trim() ? value : null;

  return (
    <div className="frequency">
      <Field label="Frequency" hint={KINDS.find((k) => k.value === current.kind)?.hint}>
        {(props) => (
          <select
            {...props}
            className="input"
            value={current.kind}
            onChange={(event) => {
              const kind = event.target.value as Frequency['kind'];
              onChange(kind === 'days' ? { kind, text } : { kind });
            }}
          >
            {KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </select>
        )}
      </Field>

      {current.kind === 'days' ? (
        <Field
          label="Which days"
          hint={
            inherited && inherited.length > 0
              ? `Leave empty to follow the plan's default schedule — ${describeBlocks(inherited)}`
              : 'Describe the days in your own words.'
          }
        >
          {(props) => (
            <input
              {...props}
              className="input"
              value={text}
              placeholder="Mondays, Wednesdays and Saturdays"
              onChange={(event) => onChange({ kind: 'days', text: event.target.value })}
            />
          )}
        </Field>
      ) : null}

      {legacy ? (
        <p className="meta">
          Previously written as &ldquo;{legacy}&rdquo;. Saving replaces it with the choice above.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The services a plan covers, chosen from the agency's catalogue.
 *
 * A checklist rather than free rows: the list is short, the vocabulary should
 * be the same across clients, and typing a service name is how two plans end up
 * meaning the same thing in two different words.
 */
export function ServiceChecklist({
  selected,
  services,
  onToggle,
}: {
  selected: Set<string>;
  services: CatalogueService[];
  onToggle: (service: CatalogueService, checked: boolean) => void;
}) {
  return (
    <div className="checklist">
      {services.map((service) => {
        const checked = selected.has(service.id);
        return (
          <label key={service.id} className="checklist__item" data-checked={checked}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => onToggle(service, event.target.checked)}
            />
            <span className="checklist__text">
              <span className="checklist__name">{service.name}</span>
              {service.description ? (
                <span className="checklist__hint">{service.description}</span>
              ) : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}
